import type {
  AgentTemplate,
  MerchantAgent,
  Project,
  ThreadMessage,
} from "./types";

// ── Layer metadata ────────────────────────────────────────────────────────

export const LAYER_LABELS: Record<string, string> = {
  executive: "Executive Layer",
  growth: "Growth Layer",
  ops: "Ops Layer",
  guest: "Guest Layer",
};

export const LAYER_ORDER = ["executive", "growth", "ops", "guest"] as const;

// ── Agent templates (Lemmon catalogue) ───────────────────────────────────

export const AGENT_TEMPLATES: AgentTemplate[] = [
  { id: "analyst",     type: "analyst",     layer: "executive", name: "Analyst",          description: "Ask about sales, revenue, promotions, orders, and reservations.",        icon: "monitoring",      tools: ["Sales summary", "Order history", "Promotions", "Reservations"],                    model: "Llama 3.3 70B" },
  { id: "coordinator", type: "coordinator", layer: "executive", name: "Coordinator",       description: "Daily briefings and cross-agent task orchestration.",                     icon: "account_tree",    tools: ["Daily briefing", "Task delegation", "Multi-agent orchestration"],                 model: "Claude Opus"   },
  { id: "marketing",   type: "marketing",   layer: "growth",    name: "Marketing",         description: "Create and schedule posts for Instagram and LinkedIn.",                   icon: "campaign",        tools: ["Instagram posts", "LinkedIn posts", "Post scheduling", "Caption drafts"],          model: "Claude Sonnet" },
  { id: "designer",    type: "designer",    layer: "growth",    name: "Designer",          description: "Generate marketing images, banners, and visual assets.",                  icon: "palette",         tools: ["Image generation", "Banner design", "Asset export"],                              model: "Claude Sonnet" },
  { id: "webdev",      type: "webdev",      layer: "growth",    name: "Webdev",            description: "Build and deploy your restaurant website using AI.",                      icon: "code",            tools: ["Edit website", "Preview changes", "Deploy", "File browser"],                       model: "Claude Sonnet" },
  { id: "seo",         type: "seo",         layer: "growth",    name: "SEO & Reputation",  description: "Manage your Google Business profile and review responses.",               icon: "search",          tools: ["Google Reviews", "SEO analysis", "Review drafts"],                                model: "Claude Sonnet" },
  { id: "revenue",     type: "revenue",     layer: "growth",    name: "Revenue Optimizer", description: "Pricing suggestions and upsell opportunity analysis.",                    icon: "trending_up",     tools: ["Pricing analysis", "Upsell suggestions", "Category mix"],                         model: "Llama 3.3 70B" },
  { id: "inventory",   type: "inventory",   layer: "ops",       name: "Inventory",         description: "Check stock levels, get low-stock alerts, and contact suppliers.",       icon: "inventory",       tools: ["Stock levels", "Low-stock alerts", "Supplier contacts"],                          model: "Llama 3.3 70B" },
  { id: "labor",       type: "labor",       layer: "ops",       name: "Labor",             description: "Shift scheduling summaries and labor cost analysis.",                    icon: "schedule",        tools: ["Shift summary", "Labor costs", "Staffing gaps"],                                   model: "Llama 3.3 70B" },
  { id: "kitchen",     type: "kitchen",     layer: "ops",       name: "Kitchen",           description: "Prep notes, allergen queries, and menu change requests.",                icon: "restaurant_menu", tools: ["Allergen lookup", "Menu changes", "Prep notes"],                                   model: "Llama 3.3 70B" },
  { id: "maintenance", type: "maintenance", layer: "ops",       name: "Maintenance",       description: "Log issues, assign priorities, and manage vendor contacts.",             icon: "build",           tools: ["Issue log", "Vendor contacts", "Priority triage"],                                model: "Llama 3.3 70B" },
  { id: "concierge",   type: "concierge",   layer: "guest",     name: "Concierge",         description: "Answer customer FAQs and handle loyalty queries.",                       icon: "support_agent",   tools: ["FAQ answers", "Loyalty lookup", "Hours & directions"],                            model: "Llama 3.3 70B" },
  { id: "reservation", type: "reservation", layer: "guest",     name: "Reservations",      description: "Manage bookings, waitlists, and deposit rules.",                         icon: "event_seat",      tools: ["Booking management", "Waitlist", "Deposit rules"],                                model: "Llama 3.3 70B" },
  { id: "support",     type: "support",     layer: "guest",     name: "Support",           description: "Handle customer complaints and guide refund workflows.",                  icon: "help",            tools: ["Complaint handling", "Refund guidance", "Escalation"],                            model: "Llama 3.3 70B" },
];

// ── Merchant agent personas ───────────────────────────────────────────────

export const STUB_AGENTS: MerchantAgent[] = [
  { id: "agent-alex", templateId: "analyst",     templateType: "analyst",     name: "Alex", avatarIcon: "monitoring", avatarColor: "violet",  description: "Knows your revenue patterns and preferred report format." },
  { id: "agent-mia",  templateId: "marketing",   templateType: "marketing",   name: "Mia",  avatarIcon: "campaign",   avatarColor: "amber",   description: "Knows your brand voice and has drafted dozens of posts." },
  { id: "agent-dev",  templateId: "webdev",       templateType: "webdev",      name: "Dev",  avatarIcon: "code",       avatarColor: "sky",     description: "Knows your site structure and has deployed 12 changes." },
  { id: "agent-sam",  templateId: "designer",     templateType: "designer",    name: "Sam",  avatarIcon: "palette",    avatarColor: "emerald", description: "Knows your visual style and has generated 30+ assets." },
  { id: "agent-lex",  templateId: "coordinator",  templateType: "coordinator", name: "Lex",  avatarIcon: "account_tree", avatarColor: "rose",  description: "Orchestrates your other agents and sends daily briefings." },
];

export const getAgent = (id: string) => STUB_AGENTS.find((a) => a.id === id);

// ── Projects ──────────────────────────────────────────────────────────────

export const STUB_PROJECTS: Project[] = [
  {
    id: "proj-summer",
    name: "Summer Campaign",
    icon: "🍋",
    color: "amber",
    description: "Social, design, and analytics for the summer menu launch.",
    agentIds: ["agent-alex", "agent-mia", "agent-sam"],
    members: [
      { userId: "user-1", name: "Anna B.",  initials: "AB", role: "owner"  },
      { userId: "user-2", name: "Marco R.", initials: "MR", role: "editor" },
    ],
    bindings: {
      r2Assets: [
        { id: "a1", name: "brand-guidelines.pdf", ext: "pdf", sizeBytes: 2_400_000, uploadedAt: "2026-05-01T10:00:00Z" },
        { id: "a2", name: "summer-menu.pdf",       ext: "pdf", sizeBytes: 1_100_000, uploadedAt: "2026-05-10T14:00:00Z" },
        { id: "a3", name: "product-photos.jpg",    ext: "jpg", sizeBytes: 5_200_000, uploadedAt: "2026-05-15T09:00:00Z" },
      ],
      artifactRepo: null,
      schedules: [],
    },
    threads: [
      { id: "thr-1", projectId: "proj-summer", title: "Build the summer campaign", createdBy: "user-1", lastMessageAt: "2026-05-25T15:30:00Z", preview: "Posted to Instagram! Banner #2 live on homepage.", participantAgentIds: ["agent-alex", "agent-mia", "agent-sam"] },
      { id: "thr-2", projectId: "proj-summer", title: "Weekend specials post",     createdBy: "user-1", lastMessageAt: "2026-05-22T16:30:00Z", preview: "3 post variations ready for review.",              participantAgentIds: ["agent-mia"] },
    ],
  },
  {
    id: "proj-website",
    name: "Restaurant Website",
    icon: "🌐",
    color: "sky",
    description: "AI-built and maintained website for Bistro Lemmon.",
    agentIds: ["agent-dev", "agent-sam", "agent-alex"],
    members: [
      { userId: "user-1", name: "Anna B.", initials: "AB", role: "owner" },
    ],
    bindings: {
      r2Assets: [
        { id: "a4", name: "menu.csv", ext: "csv", sizeBytes: 42_000, uploadedAt: "2026-05-20T10:00:00Z" },
      ],
      artifactRepo: {
        repoId: "repo-1",
        branch: "draft/thread-thr-3",
        deployedUrl: "https://bistro-lemmon.pages.dev",
        lastCommitAt: "2026-05-25T15:00:00Z",
        status: "draft",
      },
      schedules: [
        { id: "s1", name: "Weekly menu sync", cron: "0 9 * * MON", description: "Sync menu.csv to website every Monday at 9am", agentId: "agent-dev", enabled: true, lastRunAt: "2026-05-19T09:00:00Z" },
      ],
    },
    threads: [
      { id: "thr-3", projectId: "proj-website", title: "Hero section — Summer refresh",  createdBy: "user-1", lastMessageAt: "2026-05-25T15:00:00Z", preview: "Deployed to production. Site is live.", participantAgentIds: ["agent-dev", "agent-sam"] },
      { id: "thr-4", projectId: "proj-website", title: "Add online reservation form",    createdBy: "user-1", lastMessageAt: "2026-05-20T10:30:00Z", preview: "Reservation form added to /reservations.", participantAgentIds: ["agent-dev"] },
    ],
  },
  {
    id: "proj-general",
    name: "General",
    icon: "📊",
    color: "neutral",
    description: "Day-to-day questions and analysis.",
    agentIds: ["agent-alex"],
    members: [
      { userId: "user-1", name: "Anna B.", initials: "AB", role: "owner" },
    ],
    bindings: { r2Assets: [], artifactRepo: null, schedules: [
      { id: "s2", name: "Daily sales briefing", cron: "0 8 * * *", description: "Send daily sales summary every morning at 8am", agentId: "agent-alex", enabled: true, lastRunAt: "2026-05-25T08:00:00Z" },
    ] },
    threads: [
      { id: "thr-5", projectId: "proj-general", title: "Monthly revenue Q2",       createdBy: "user-1", lastMessageAt: "2026-05-25T14:30:00Z", preview: "Revenue was CHF 124,300, up 12% vs Q1.",          participantAgentIds: ["agent-alex"] },
      { id: "thr-6", projectId: "proj-general", title: "Yesterday's sales",         createdBy: "user-1", lastMessageAt: "2026-05-24T18:45:00Z", preview: "Total: CHF 4,230 across 87 orders.",               participantAgentIds: ["agent-alex"] },
    ],
  },
];

// ── Stub messages (typed output blocks) ──────────────────────────────────

export const STUB_MESSAGES: Record<string, ThreadMessage[]> = {
  "thr-1": [
    {
      id: "m1", senderType: "user", senderId: "user-1", senderName: "Anna B.", senderInitials: "AB",
      outputs: [{ type: "markdown", renderer: "markdown", data: { content: "Can we do something fun for the summer launch? I want social posts, some nice banners, and a homepage update." } }],
      createdAt: "2026-05-25T14:00:00Z",
    },
    {
      id: "m2", senderType: "agent", senderId: "agent-alex", senderName: "Alex", senderAvatarIcon: "monitoring", senderAvatarColor: "violet",
      outputs: [
        { type: "markdown", renderer: "markdown", data: { content: "Before we start — here's what's worth featuring. These are your top-selling items this month:" } },
        { type: "chart_bar", renderer: "chart_bar", data: { title: "Top dishes by revenue (May 2026)", labels: ["Burger CHF", "Tiramisu", "Pasta", "Steak", "Salad"], values: [24300, 18100, 14200, 12800, 9400], unit: "CHF" } },
        { type: "markdown", renderer: "markdown", data: { content: "The **burger and tiramisu** are clear winners. I'd lead with both in any summer creative." } },
      ],
      createdAt: "2026-05-25T14:01:00Z",
    },
    {
      id: "m3", senderType: "agent", senderId: "agent-sam", senderName: "Sam", senderAvatarIcon: "palette", senderAvatarColor: "emerald",
      outputs: [
        { type: "markdown", renderer: "markdown", data: { content: "Based on Alex's data, here are 3 banner options for the summer launch:" } },
        { type: "image_gallery", renderer: "image_gallery", data: { images: [
          { id: "img-1", url: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600", alt: "Burger banner", caption: "Option A — Burger hero" },
          { id: "img-2", url: "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=600", alt: "Tiramisu banner", caption: "Option B — Tiramisu close-up" },
          { id: "img-3", url: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600", alt: "Table banner", caption: "Option C — Lifestyle" },
        ] } },
      ],
      createdAt: "2026-05-25T14:02:00Z",
    },
    {
      id: "m4", senderType: "agent", senderId: "agent-mia", senderName: "Mia", senderAvatarIcon: "campaign", senderAvatarColor: "amber",
      outputs: [
        { type: "markdown", renderer: "markdown", data: { content: "And here's the Instagram post draft for the launch, using banner Option A:" } },
        { type: "instagram_card", renderer: "instagram_card", data: {
          caption: "☀️ Summer is here and so is our new menu! Fresh flavours, bold ingredients — starting with our legendary burger. Book your table now, link in bio. 🍋",
          hashtags: ["#summer", "#bistrolemmon", "#summermenu", "#foodie"],
          imageUrl: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600",
          scheduledFor: null,
        } },
        { type: "approval_actions", renderer: "approval_actions", data: {
          question: "Ready to post?",
          actions: [
            { id: "post_now",  label: "Post now",      style: "primary" },
            { id: "schedule",  label: "Schedule…",     style: "ghost" },
            { id: "revise",    label: "Revise caption", style: "ghost" },
          ],
        } },
      ],
      createdAt: "2026-05-25T14:03:00Z",
    },
  ],

  "thr-3": [
    {
      id: "m1", senderType: "user", senderId: "user-1", senderName: "Anna B.", senderInitials: "AB",
      outputs: [{ type: "markdown", renderer: "markdown", data: { content: 'Update the homepage hero to say "Summer Menu Now Live" and swap in the new banner image.' } }],
      createdAt: "2026-05-25T14:30:00Z",
    },
    {
      id: "m2", senderType: "agent", senderId: "agent-dev", senderName: "Dev", senderAvatarIcon: "code", senderAvatarColor: "sky",
      outputs: [
        { type: "markdown", renderer: "markdown", data: { content: "Reading current homepage and making the update…" } },
        { type: "website_diff", renderer: "website_diff", data: {
          file: "index.html",
          before: '<h1 class="hero-title">Welcome to Bistro Lemmon</h1>\n<img src="/assets/hero-old.jpg" />',
          after:  '<h1 class="hero-title">Summer Menu Now Live ☀️</h1>\n<img src="/assets/banner-summer.jpg" />',
        } },
        { type: "website_preview", renderer: "website_preview", data: { url: "https://draft-thr-3.bistro-lemmon.pages.dev", branch: "draft/thread-thr-3" } },
        { type: "approval_actions", renderer: "approval_actions", data: {
          question: "Ready to deploy to production?",
          actions: [
            { id: "deploy",  label: "Deploy",           style: "primary" },
            { id: "revise",  label: "Change something", style: "ghost" },
          ],
        } },
      ],
      createdAt: "2026-05-25T14:31:00Z",
    },
    {
      id: "m3", senderType: "user", senderId: "user-1", senderName: "Anna B.", senderInitials: "AB",
      outputs: [{ type: "markdown", renderer: "markdown", data: { content: "Looks great, deploy it." } }],
      createdAt: "2026-05-25T14:45:00Z",
    },
    {
      id: "m4", senderType: "agent", senderId: "agent-dev", senderName: "Dev", senderAvatarIcon: "code", senderAvatarColor: "sky",
      outputs: [
        { type: "artifact", renderer: "artifact", data: {
          artifactId: "artifact-1",
          name: "Homepage — Summer refresh",
          type: "website",
          status: "deployed",
          previewUrl: "https://bistro-lemmon.pages.dev",
          deployedUrl: "https://bistro-lemmon.pages.dev",
        } },
        { type: "markdown", renderer: "markdown", data: { content: "Deployed! Your site is live at **https://bistro-lemmon.pages.dev** with the updated hero." } },
      ],
      createdAt: "2026-05-25T14:46:00Z",
    },
  ],

  "thr-5": [
    {
      id: "m1", senderType: "user", senderId: "user-1", senderName: "Anna B.", senderInitials: "AB",
      outputs: [{ type: "markdown", renderer: "markdown", data: { content: "What was total revenue for Q2 so far, and how does it compare to last year?" } }],
      createdAt: "2026-05-25T14:00:00Z",
    },
    {
      id: "m2", senderType: "agent", senderId: "agent-alex", senderName: "Alex", senderAvatarIcon: "monitoring", senderAvatarColor: "violet",
      outputs: [
        { type: "markdown", renderer: "markdown", data: { content: "Q2 revenue (April – May 25) was **CHF 124,300**, up **14.1%** vs Q2 last year (CHF 108,900)." } },
        { type: "chart_bar", renderer: "chart_bar", data: {
          title: "Q2 Revenue comparison",
          labels: ["Q2 2025", "Q2 2026 (to date)"],
          values: [108900, 124300],
          unit: "CHF",
        } },
        { type: "data_table", renderer: "data_table", data: {
          columns: [{ key: "month", label: "Month" }, { key: "revenue", label: "Revenue" }, { key: "orders", label: "Orders" }],
          rows: [
            { month: "April 2026", revenue: "CHF 58,400", orders: 1240 },
            { month: "May 2026 (to date)", revenue: "CHF 65,900", orders: 1380 },
          ],
        } },
      ],
      createdAt: "2026-05-25T14:01:00Z",
    },
  ],
};
